import React from 'react';
import { useAuthForm } from '../../hooks/useAuthForm';
import Modal from './Modal';
import FormField from './FormField';
import AlertMessage from './AlertMessage';
import Button from './Button';

export interface EntityConfig {
  entityName: string; // "file", "folder"
  titles: {
    create?: string;
    edit: string;
  };
  description?: {
    create?: string;
    edit?: string;
  };
  fieldLabel: string; // "File Name", "Folder Name"
  placeholder: string; // "Enter file name", "Enter folder name"
  helperText?: string;
  validation?: (value: string) => string | null;
}

interface EntityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string }) => Promise<void>;
  mode?: 'create' | 'edit';
  initialData?: { name: string };
  config: EntityConfig;
}

const EntityModal: React.FC<EntityModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode = 'edit',
  initialData,
  config,
}) => {
  const {
    formData,
    handleInputChange,
    isLoading,
    setIsLoading,
    error,
    setError,
    resetForm,
    setFormData,
  } = useAuthForm<{ name: string }>({
    name: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Basic validation
    const name = formData.name.trim();
    if (!name) {
      setError(`${config.fieldLabel} is required`);
      return;
    }

    // Custom validation if provided
    if (config.validation) {
      const validationError = config.validation(name);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    setIsLoading(true);

    try {
      await onSubmit({ name });
      resetForm();
      onClose();
    } catch (err: any) {
      setError(err.message || `Failed to ${mode} ${config.entityName}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  React.useEffect(() => {
    if (isOpen) {
      setFormData({
        name: initialData && mode === 'edit' ? initialData.name : '',
      });
      setError('');
    }
  }, [initialData, isOpen, mode, setFormData, setError]);

  const getTitle = () => {
    if (mode === 'create' && config.titles.create) {
      return config.titles.create;
    }
    return config.titles.edit;
  };

  const getDescription = () => {
    if (mode === 'create' && config.description?.create) {
      return config.description.create;
    }
    return config.description?.edit;
  };

  const getSubmitText = () => {
    if (mode === 'create') {
      return `Create ${config.entityName.charAt(0).toUpperCase() + config.entityName.slice(1)}`;
    }
    return 'Save Changes';
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={getTitle()}>
      <div className="space-y-4">
        {getDescription() && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {getDescription()}
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <AlertMessage type="error" message={error} show={!!error} />

          <FormField
            id="name"
            name="name"
            type="text"
            label={config.fieldLabel}
            placeholder={config.placeholder}
            value={formData.name}
            onChange={handleInputChange}
            required
            disabled={isLoading}
            autoFocus={isOpen}
          />

          {config.helperText && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {config.helperText}
            </p>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !formData.name.trim()}
              loading={isLoading}
            >
              {getSubmitText()}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default EntityModal;
